/* @flow */
import type { ProductType, ProductID } from './data/Products';

function getDefaultMetadata(
  title: string,
  description: string
): { name?: string, property?: string, content: string }[] {
  return [
    { name: 'description', content: description },
    { property: 'fb:app_id', content: '276908692510636' },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:image', content: 'https://lugg.com/social.png' },
    { property: 'og:type', content: 'business.business' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:image', content: 'https://lugg.com/social.png' },
    { name: 'twitter:site', content: '@lugg' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
  ];
}

function getDefaultLinks() {
  return [];
}

export function copyProperties(source: Object, target: Object) {
  const KEYS_TO_IGNORE_WHEN_COPYING_PROPERTIES = [
    'arguments',
    'callee',
    'caller',
    'length',
    'name',
    'prototype',
    'type',
  ];

  Object.getOwnPropertyNames(source).forEach(key => {
    if (
      KEYS_TO_IGNORE_WHEN_COPYING_PROPERTIES.indexOf(key) < 0 &&
      !Object.prototype.hasOwnProperty.call(target, key)
    ) {
      const descriptor = Object.getOwnPropertyDescriptor(source, key);
      Object.defineProperty(target, key, descriptor);
    }
  });
}

export function getMetadataFromComponents(
  components: React$ComponentType<*>[],
  routeProps: Object
) {
  const DefaultTitle = 'Lugg: On-Demand Moving & Furniture Delivery';
  const DefaultDescription =
    'On-demand movers and a truck in under an hour to load, haul and deliver anything for you. Get an estimate and try it today!';

  let metadata;
  for (let i = components.length - 1; i >= 0; i--) {
    if (components[i]) {
      metadata = components[i].metadata;
      if (metadata) {
        break;
      }
    }
  }

  if (typeof metadata === 'function') {
    metadata = metadata(routeProps);
  }
  let title = DefaultTitle;
  if (metadata && metadata.title) {
    title = `${metadata.title} - Lugg`;
  }
  let description = (metadata && metadata.description) || DefaultDescription;
  let customMeta = metadata && metadata.meta;
  if (!customMeta) customMeta = [];
  let defaultMeta = getDefaultMetadata(title, description);
  let customMetaKeys = customMeta.map(m => m.name || m.property);
  let link = metadata && metadata.link ? [metadata.link, ...getDefaultLinks()] : getDefaultLinks();

  let meta = [
    ...customMeta,
    ...defaultMeta.reduce((memo, value) => {
      const key = value.name || value.property;
      if (!customMetaKeys.includes(key)) memo.push(value);
      return memo;
    }, []),
  ];

  return {
    title,
    meta,
    link,
  };
}

export function pluck(keys: Array<string>, object: Object) {
  return keys.reduce((memo, value) => {
    memo[value] = object[value];
    return memo;
  }, {});
}

export function chunk(array: Array<any>, size: number) {
  let chunks = [];
  let i = 0;
  let n = array.length;

  while (i < n) {
    chunks.push(array.slice(i, (i += size)));
  }

  return chunks;
}

export function getProductValues(productMap: { [id: ProductID]: ProductType }): ProductType[] {
  return Object.keys(productMap).map(id => productMap[id]);
}

export function debounce(fn: Function, delay: number) {
  let timer = null;
  return function(...args: any) {
    let context = this;
    clearTimeout(timer);
    timer = setTimeout(function() {
      fn.apply(context, args);
    }, delay);
  };
}

export function zip(arr: [], arr2: []): [] {
  return arr.map((item, index) => {
    return [item, arr2[index]];
  });
}

export function split(arr: [], size: number): [] {
  let splitBy = Math.ceil(arr.length / size);
  return chunk(arr, splitBy);
}

export function flatten(arr: []) {
  return arr.reduce(function(flat, toFlatten) {
    return flat.concat(Array.isArray(toFlatten) ? flatten(toFlatten) : toFlatten);
  }, []);
}

export function compact(arr: Array<any>): Array<any> {
  return arr.filter(item => !!item);
}

export function prefetchImage(url: string) {
  let img = new window.Image();
  img.src = url;
}

// https://stackoverflow.com/questions/3115982/how-to-check-if-two-arrays-are-equal-with-javascript
export function arraysEqual(a: [], b: []) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.length !== b.length) return false;

  // If you don't care about the order of the elements inside
  // the array, you should sort both arrays here.

  for (let i = 0; i < a.length; ++i) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

export function ellipsis(str: string, length: number = 140) {
  if (str.length > 140) {
    return `${str.substring(0, length)}...`;
  } else {
    return str;
  }
}

export function titleize(str: string) {
  return str.toLowerCase().replace(/(?:^|\s|-|_)\S/g, function(c) {
    return c.replace(/(_)/g, ' ').toUpperCase();
  });
}

export function pluralize(num: number, singular: string, plural: string): string {
  return num === 1 ? singular : plural;
}

export function loadJS(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.async = true;
    script.src = src;
    script.addEventListener('load', resolve);
    script.addEventListener('error', () => reject(new Error('Error loading script.')));
    script.addEventListener('abort', () => reject(new Error('Script loading aborted.')));
    document.head.appendChild(script);
  });
}

export function formatDollars(cents: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

export function formatWholeDollars(cents: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}



// WEBPACK FOOTER //
// ./src/utils.js